import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileSpreadsheet, FileText, Table2 } from 'lucide-react';
import dayjs from 'dayjs';

import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import type { DashboardData, DateRange } from '../../../../common/hooks/use-analytics-api.hook';
import { Toast } from '../../../../components/design-system/common';
import { AnalyticsPdfReport } from './AnalyticsPdfReport';

export interface ExportKpi {
  label: string;
  value: string;
  change: number;
}

/**
 * PDF, Excel and CSV of the analytics overview. PDF and Excel mount the
 * off-screen report and capture it (html2canvas cannot read cross-origin
 * images, so product thumbnails are prefetched as data URLs first).
 */
export function DashboardExportMenu({ data, dateRange, kpis }: { data: DashboardData | null; dateRange: DateRange; kpis: ExportKpi[] }) {
  const { t } = useTranslation();
  const [exporting, setExporting] = useState<null | 'pdf' | 'xlsx'>(null);
  const [failed, setFailed] = useState(false);
  const [thumbnails, setThumbnails] = useState<Record<string, string> | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);
  const R = (suffix: string) => t(`ANALYTICS.MODERN_ANALYTICS.EXPORT_REPORT.${suffix}`);
  const fileBase = `analytics_${dateRange.startDate}_${dateRange.endDate}`;

  const b = data?.engagementBreakdown;
  const engagementRows = b
    ? [
        { name: t('ANALYTICS.MODERN_ANALYTICS.CHART_LABELS.SPACE_SELECTION'), value: b.spaceSelection || 0 },
        { name: t('ANALYTICS.MODERN_ANALYTICS.CHART_LABELS.PRODUCT_CHANGE'), value: b.productChange || 0 },
        { name: t('ANALYTICS.MODERN_ANALYTICS.CHART_LABELS.UPLOADS'), value: b.uploads || 0 },
        { name: t('ANALYTICS.MODERN_ANALYTICS.CHART_LABELS.SEARCH'), value: b.search || 0 },
        { name: t('ANALYTICS.MODERN_ANALYTICS.CHART_LABELS.ROTATION'), value: b.rotation || 0 },
        { name: t('ANALYTICS.MODERN_ANALYTICS.CHART_LABELS.COMPARE'), value: b.compare || 0 },
        { name: t('ANALYTICS.MODERN_ANALYTICS.CHART_LABELS.DOWNLOAD'), value: b.download || 0 },
      ].sort((x, y) => y.value - x.value)
    : [];
  const engagementTotal = engagementRows.reduce((s, r) => s + r.value, 0);

  const exportCsv = () => {
    if (!data) return;
    const rows: any[][] = [[R('REPORT_TITLE'), `${dateRange.startDate} - ${dateRange.endDate}`], []];
    const section = (title: string, body: any[][]) => {
      if (body.length <= 1) return;
      rows.push([title], ...body, []);
    };
    section(t('ANALYTICS.MODERN_ANALYTICS.KEY_PERFORMANCE_METRICS'), [[R('METRIC'), R('VALUE'), R('CHANGE')], ...kpis.map((k) => [k.label, k.value, k.change])]);
    section(t('ANALYTICS.MODERN_ANALYTICS.VISITS_TREND'), [[R('DATE'), R('VISITS')], ...(data.trends?.visits ?? []).map((i) => [dayjs(i.date).format('YYYY-MM-DD'), i.value])]);
    section(t('ANALYTICS.MODERN_ANALYTICS.ENGAGEMENT_TYPES'), [
      [R('ENGAGEMENT_TYPE'), R('COUNT'), R('SHARE')],
      ...engagementRows.map((r) => [r.name, r.value, engagementTotal ? Number(((r.value / engagementTotal) * 100).toFixed(1)) : 0]),
    ]);
    section(R('USER_JOURNEY'), [
      [R('STAGE'), R('USERS'), R('CONVERSION_RATE')],
      ...(data.userJourney?.stages ?? []).map((s) => [s.stage, s.users, Number((s.conversionRate || 0).toFixed(1))]),
    ]);
    section(t('ANALYTICS.MODERN_ANALYTICS.TOP_PERFORMING_PRODUCTS'), [
      [R('PRODUCT'), R('SKU'), R('VIEWS'), R('INTERACTIONS'), R('DOWNLOADS'), R('CONVERSION_RATE'), R('ENGAGEMENT_RATE'), R('UNIQUE_USERS')],
      ...(data.topProducts ?? []).map((p) => [
        p.name,
        p.sku || '',
        p.metrics.totalViews,
        p.metrics.totalInteractions,
        p.metrics.totalDownloads,
        Number((p.metrics.conversionRate || 0).toFixed(1)),
        Number((p.metrics.engagementRate || 0).toFixed(1)),
        p.metrics.uniqueUserCount ?? '',
      ]),
    ]);
    section(R('TOP_CITIES'), [
      [R('CITY'), R('COUNTRY'), R('UNIQUE_USERS'), R('EVENTS')],
      ...(data.geographicMetrics?.topCities ?? []).map((c) => [c.city, c.country, c.uniqueUsers, c.events]),
    ]);
    section(R('TOP_COUNTRIES'), [
      [R('COUNTRY'), R('UNIQUE_USERS'), R('EVENTS')],
      ...(data.geographicMetrics?.topCountries ?? []).map((c) => [c.country, c.uniqueUsers, c.events]),
    ]);
    const cell = (v: any) => {
      const s = String(v ?? '');
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    download(new Blob(['﻿' + rows.map((r) => r.map(cell).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8;' }), `${fileBase}.csv`);
  };

  useEffect(() => {
    if (!exporting || !data) return;
    const toDataUrl = async (url: string): Promise<string | null> => {
      try {
        const res = await fetch(url, { mode: 'cors' });
        if (!res.ok) return null;
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    };
    const run = async () => {
      try {
        const thumbs: Record<string, string> = {};
        if (exporting === 'pdf') {
          const entries = await Promise.all(
            (data.topProducts || []).slice(0, 10).map(async (p) => [p.productId, p.thumbnailPath ? await toDataUrl(p.thumbnailPath) : null] as const),
          );
          entries.forEach(([id, url]) => url && (thumbs[id] = url));
        }
        setThumbnails(thumbs);
        await (document as any).fonts?.ready;
        await new Promise((r) => setTimeout(r, 600));
        const root = reportRef.current;
        if (!root) throw new Error('Report did not render');
        const { default: html2canvas } = await import('html2canvas');
        if (exporting === 'pdf') {
          const pages = Array.from(root.querySelectorAll<HTMLElement>('[data-pdf-page]'));
          if (!pages.length) throw new Error('PDF report did not render');
          const { default: JsPdf } = await import('jspdf');
          const pdf = new JsPdf({ orientation: 'portrait', unit: 'mm', format: 'a4' });
          for (let i = 0; i < pages.length; i++) {
            const canvas = await html2canvas(pages[i], { scale: 2, backgroundColor: '#FFFFFF', logging: false });
            if (i > 0) pdf.addPage();
            pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, 210, 297);
          }
          pdf.save(`${fileBase}.pdf`);
        } else {
          const capture = async (id: string) => {
            const el = root.querySelector<HTMLElement>(`[data-chart="${id}"]`);
            if (!el) return null;
            const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#FFFFFF', logging: false });
            return { dataUrl: canvas.toDataURL('image/png'), width: el.offsetWidth, height: el.offsetHeight };
          };
          const { exportAnalyticsXlsx } = await import('../utils/exportAnalyticsXlsx');
          await exportAnalyticsXlsx({
            fileBase,
            period: `${dateRange.startDate} - ${dateRange.endDate}`,
            data,
            kpis,
            engagementRows,
            engagementTotal,
            charts: {
              visits: await capture('visits-trend'),
              engagements: await capture('engagements-trend'),
              engagementTypes: await capture('engagement-types'),
            },
            t,
          });
        }
      } catch (e) {
        console.error(`${exporting} export failed`, e);
        setFailed(true);
      } finally {
        setExporting(null);
        setThumbnails(null);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exporting]);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5" disabled={!data || !!exporting} data-testid="analytics-export">
            <Download />
            <span className="hidden sm:inline">{exporting ? R('EXPORTING') : t('VIZ_ANALYTICS.COMMON.EXPORT')}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-44">
          <DropdownMenuItem onClick={() => setExporting('pdf')}>
            <FileText />
            {R('FORMAT_PDF')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setExporting('xlsx')}>
            <FileSpreadsheet />
            {R('FORMAT_XLSX')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={exportCsv}>
            <Table2 />
            {R('FORMAT_CSV')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {exporting && thumbnails && data && (
        <div ref={reportRef} style={{ position: 'fixed', top: 0, left: -10000, zIndex: -1, pointerEvents: 'none' }} aria-hidden="true">
          <AnalyticsPdfReport data={data} dateRange={dateRange} thumbnails={thumbnails} />
        </div>
      )}
      <Toast message={R('EXPORT_FAILED')} type="error" isVisible={failed} onClose={() => setFailed(false)} />
    </>
  );
}

export function download(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
